import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { userTemplateService, UserEmailTemplate } from '../services/userTemplateService';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { sendingDomainService } from '../services/sendingDomainService';
import { emailVerificationService } from '../services/emailVerificationService';

interface SimpleEmailActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: EmailActionConfig) => void;
  initialConfig?: Partial<EmailActionConfig>;
}

export interface EmailActionConfig {
  emailName: string;
  subject: string;
  preheader: string;
  fromName: string;
  fromEmail: string;
  templateId: string;
  templateName: string;
  templateType: 'system' | 'user';
}

const SimpleEmailActionModal: React.FC<SimpleEmailActionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialConfig
}) => {
  const { currentUser } = useAuth();
  const { activeUserId, currentWorkspace } = useWorkspace();
  // Senders and templates belong to the account being viewed, which is the
  // impersonated user when an admin has entered a profile.
  const ownerId = activeUserId || currentUser?.uid;
  const [config, setConfig] = useState<EmailActionConfig>({
    emailName: '',
    subject: '',
    preheader: '',
    fromName: '',
    fromEmail: '',
    templateId: '',
    templateName: '',
    templateType: 'system',
    ...initialConfig
  });

  const [userTemplates, setUserTemplates] = useState<UserEmailTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<UserEmailTemplate | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);
  const [fromEmailOptions, setFromEmailOptions] = useState<Array<{ label: string; value: string; status: string }>>([]);

  // Load templates
  useEffect(() => {
    if (isOpen) {
      console.log('SimpleEmailActionModal: Loading templates, currentUser:', currentUser?.uid, 'workspaceId:', currentWorkspace?.id);
      loadTemplates();
    }
  }, [isOpen, currentUser, currentWorkspace]);

  // Load sending domains for from email dropdown
  useEffect(() => {
    const loadSendingAddresses = async () => {
      if (ownerId && isOpen) {
        try {
          console.log('Loading verified email addresses for user:', ownerId);
          
          // Load verified email addresses
          const verifiedEmails = await emailVerificationService.getUserVerifiedEmails(ownerId);
          console.log('Verified emails loaded:', verifiedEmails);
          
          // Build from email options using the actual sending addresses
          const options: Array<{ label: string; value: string; status: string }> = [];
          
          // For each verified email, get its sending address
          for (const emailVerification of verifiedEmails) {
            if (emailVerification.status === 'verified') {
              try {
                // Get the actual sending address for this email
                const sendingAddress = await sendingDomainService.getSendingAddressForEmail(
                  ownerId,
                  emailVerification.email
                );
                
                console.log(`Sending address for ${emailVerification.email}:`, sendingAddress);
                
                // Show only the sending address
                options.push({
                  label: sendingAddress,
                  value: sendingAddress,
                  status: 'verified'
                });
              } catch (error) {
                console.error(`Error getting sending address for ${emailVerification.email}:`, error);
              }
            }
          }
          
          console.log('From email options built:', options.length, options);
          setFromEmailOptions(options);
        } catch (error) {
          console.error('Error loading sending addresses:', error);
        }
      }
    };
    
    loadSendingAddresses();
  }, [currentUser, ownerId, isOpen]);

  const loadTemplates = async () => {
    console.log('loadTemplates called, currentUser.uid:', currentUser?.uid, 'workspaceId:', currentWorkspace?.id);
    setLoading(true);
    try {
      if (currentUser?.uid && currentWorkspace?.id) {
        console.log('Fetching user templates for user:', currentUser.uid, 'workspace:', currentWorkspace.id);
        const templates = await userTemplateService.getUserTemplates(currentUser.uid, currentWorkspace.id);
        console.log('User templates loaded:', templates.length);
        setUserTemplates(templates);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = async (template: UserEmailTemplate) => {
    setSelectedTemplate(template);
    
    // Select user template directly
    setConfig(prev => ({
      ...prev,
      templateId: template.id!,
      templateName: template.name,
      templateType: 'user',
      subject: prev.subject || template.subject || '',
    }));
    
    // Load preview
    setLoadingPreview(true);
    setPreviewHtml(template.htmlContent || '<div style="padding: 20px; text-align: center; color: #666;">No preview available</div>');
    setLoadingPreview(false);
  };

  const handleSave = () => {
    if (!config.subject || !config.fromEmail || !config.templateId) {
      alert('Please fill in all required fields and select a template');
      return;
    }
    onSave(config);
    onClose();
  };

  // Load initial template preview if one is already selected
  useEffect(() => {
    if (initialConfig?.templateId && isOpen) {
      const loadInitialTemplate = async () => {
        setLoadingPreview(true);
        try {
          const found = userTemplates.find(t => t.id === initialConfig.templateId);
          if (found) {
            setSelectedTemplate(found);
            setPreviewHtml(found.htmlContent || '<div style="padding: 20px; text-align: center; color: #666;">No preview available</div>');
          }
        } catch (error) {
          console.error('Error loading initial template:', error);
        } finally {
          setLoadingPreview(false);
        }
      };
      loadInitialTemplate();
    }
  }, [initialConfig?.templateId, isOpen, userTemplates]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 py-6">
      <div className="w-full max-w-7xl bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-neutral-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Configure Email</h3>
            <p className="text-sm text-gray-500 dark:text-neutral-400 mt-0.5">Choose a template and customize your email</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          <div className="grid md:grid-cols-3 divide-x divide-gray-200 dark:divide-neutral-700 min-h-full">
            {/* Left: Template Selection */}
            <div className="p-6 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Your Templates</h4>

                {/* Template Card Grid */}
                <div className="grid grid-cols-3 gap-3 max-h-[500px] overflow-y-auto pr-2">
                  {loading ? (
                    <div className="text-center py-8 text-gray-500">Loading templates...</div>
                  ) : userTemplates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-neutral-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm">No saved templates yet.</p>
                      <p className="text-xs mt-1">Create templates in the Email Templates section</p>
                    </div>
                  ) : (
                    userTemplates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleTemplateSelect(template)}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                          selectedTemplate?.id === template.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                            : 'border-gray-200 dark:border-neutral-700 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm'
                        }`}
                      >
                        <div className="space-y-2">
                          {/* Template thumbnail/preview */}
                          <div className="w-full h-24 bg-gray-100 dark:bg-neutral-800 rounded overflow-hidden mb-2">
                            {template.htmlContent ? (
                              <iframe
                                title={`preview-${template.id}`}
                                srcDoc={template.htmlContent}
                                className="w-full h-full border-0 pointer-events-none"
                                style={{ transform: 'scale(0.25)', transformOrigin: 'top left', width: '400%', height: '400%' }}
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full text-gray-400">
                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}
                          </div>
                          
                          {/* Template info */}
                          <div>
                            <div className="font-medium text-sm text-gray-900 dark:text-white truncate">{template.name}</div>
                            {template.subject && (
                              <div className="text-xs text-gray-500 dark:text-neutral-400 mt-1 truncate">{template.subject}</div>
                            )}
                          </div>
                          
                          {/* Selection indicator */}
                          {selectedTemplate?.id === template.id && (
                            <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 font-medium">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Selected
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Middle: Preview */}
            <div className="p-6 space-y-4 bg-gray-50 dark:bg-neutral-800/50">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Preview</h4>
                <div className="bg-white dark:bg-neutral-900 rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden" style={{ height: '500px' }}>
                  {loadingPreview ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-sm text-gray-500">Loading preview...</div>
                    </div>
                  ) : selectedTemplate ? (
                    <iframe
                      title="template-preview"
                      srcDoc={previewHtml}
                      className="w-full h-full border-0"
                      style={{ transform: 'scale(0.7)', transformOrigin: 'top left', width: '142.857%', height: '142.857%' }}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center p-6">
                        <svg className="w-16 h-16 mx-auto text-gray-300 dark:text-neutral-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <p className="text-sm text-gray-500 dark:text-neutral-400">Select a template to preview</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Configuration */}
            <div className="p-6 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Email Details</h4>
                
                <div className="space-y-4">
                  {/* Email Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Email Name
                    </label>
                    <input
                      type="text"
                      value={config.emailName}
                      onChange={(e) => setConfig(prev => ({ ...prev, emailName: e.target.value }))}
                      placeholder="e.g., Welcome Email #1"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
                    />
                    <p className="text-xs text-gray-500 dark:text-neutral-400 mt-1">Internal name for this email</p>
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Subject Line <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={config.subject}
                      onChange={(e) => setConfig(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Welcome aboard, {{first_name}}!"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* Preheader */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Preheader
                    </label>
                    <input
                      type="text"
                      value={config.preheader}
                      onChange={(e) => setConfig(prev => ({ ...prev, preheader: e.target.value }))}
                      placeholder="Short summary text"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* From Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      From Name
                    </label>
                    <input
                      type="text"
                      value={config.fromName}
                      onChange={(e) => setConfig(prev => ({ ...prev, fromName: e.target.value }))}
                      placeholder="Your Brand"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
                    />
                  </div>

                  {/* From Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      From Email <span className="text-red-500">*</span>
                    </label>
                    {fromEmailOptions.length > 0 ? (
                      <select
                        value={config.fromEmail}
                        onChange={(e) => setConfig(prev => ({ ...prev, fromEmail: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
                      >
                        <option value="">Select sender email...</option>
                        {fromEmailOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="email"
                        value={config.fromEmail}
                        onChange={(e) => setConfig(prev => ({ ...prev, fromEmail: e.target.value }))}
                        placeholder="hello@yourdomain.com"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-neutral-800 text-gray-900 dark:text-white"
                      />
                    )}
                    <p className="text-xs text-gray-500 dark:text-neutral-400 mt-1">
                      {fromEmailOptions.length > 0
                        ? `${fromEmailOptions.length} verified sender ${fromEmailOptions.length === 1 ? 'address' : 'addresses'}` 
                        : 'No verified sender addresses. Add one in Settings → Sender Verification'}
                    </p>
                  </div>

                  {/* Selected Template Info */}
                  {selectedTemplate && (
                    <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="text-sm font-medium text-blue-900 dark:text-blue-300">
                        Selected: {selectedTemplate.name}
                      </div>
                      <div className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                        Your Template
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-800/50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!config.subject || !config.fromEmail || !config.templateId}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            Save Email Configuration
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleEmailActionModal;
